<template>
  <div :class="classList" class="service-selector">
    <confirm-dialog
      v-if="confirmDialog.isShow"
      :data="confirmDialog"
      @confirm="handleConfirmRemoveAll"
    />
    <searchable-service-list
      v-if="showSearchList"
      ref="searchableServiceList"
      :query="query"
      :is-loading="isLoading"
      :value="selectableServices"
      :disabled="disabled"
      :show-add-all="showAddAll"
      :has-more-services="hasMoreServices"
      class="search-list"
      @queryChange="handleQueryChange"
      @addAll="handleClickAddAllButton"
      @select="handleSelectService"
      @getMoreServices="setupServices"
    ></searchable-service-list>
    <selected-service-list
      :disabled="disabled"
      :value="value"
      class="selected-list"
      @remove="handleRemoveService"
      @removeAll="handleRemoveAllService"
    ></selected-service-list>
  </div>
</template>

<script>
import { baanRueJaiClient } from "@/dependencies";
import { debounce } from "@/lib/util";
import differenceWith from "ramda/src/differenceWith";
import ConfirmDialog from "@/components/helper/dialog/ConfirmDialog";
import SearchableServiceList from "./SearchableServiceList";
import SelectedServiceList from "./SelectedServiceList";

function mapService(service) {
  return {
    id: service.id,
    iconUrl: service.icon_url,
    name: service.name,
    shortDescription: service.short_description,
    description: service.description,
    provider: { ...service.provider, logoUrl: service.provider.logo_url },
    options: service.options,
    quota: service.quota
  };
}

export default {
  components: {
    ConfirmDialog,
    SelectedServiceList,
    SearchableServiceList,
  },
  props: {
    initialServices: { type: Array, default: null },
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    showAddAll: { type: Boolean, default: false }
  },
  data() {
    return {
      query: "",
      allServices: [],
      isLoading: false,
      page: 1,
      hasMoreServices: false,
      totalServices: 1,
      confirmDialog: {
        isShow: false,
        title: "ลบรายการบริการ",
        detail: "คุณต้องการลบรายการบริการใช่หรือไม่",
        buttonSubmit: "ลบ",
        buttonCancel: "ยกเลิก"
      }
    };
  },
  computed: {
    selectableServices() {
      return differenceWith(this.compareService, this.allServices, this.value);
    },
    showSearchList() {
      return !this.disabled;
    },
    classList() {
      return { disabled: this.disabled };
    },
    queryParams() {
      return {
        name: this.query,
        page: this.page
      };
    }
  },
  watch: {
    query() {
      this.watchQuery();
    }
  },
  created() {
    this.setupServices();
  },
  methods: {
    async setupServices() {
      if (this.allServices.length === this.totalServices) {
        this.hasMoreServices = false;
        return;
      }

      try {
        const services = await this.fetchServices(this.queryParams);
        this.page += 1;
        this.hasMoreServices = true;
        this.allServices = this.allServices.concat(services.map(mapService));
      } catch {
        this.isLoading = false;
      }
    },
    async fetchServices(params = {}) {
      if (this.initialServices) {
        return this.initialServices;
      }

      this.isLoading = true;
      const response = await baanRueJaiClient.admin.subscriptions.services.search(params);
      this.isLoading = false;
      this.totalServices = response.data.result.count;
      return response.data.result.services;
    },
    handleSelectService(service) {
      this.updateInput([...this.value, service]);
    },
    async handleClickAddAllButton() {
      this.isLoading = true;
      try {
        let rawServices;
        if (!this.query) {
          const response = await baanRueJaiClient.admin.subscriptions.services.list();
          rawServices = response.data.result;
        } else {
          const response = await baanRueJaiClient.admin.subscriptions.services.search({ name: this.query, take: 0 });
          rawServices = response.data.result.services;
        }
        this.updateInput([...this.value, ...rawServices.map(mapService)]);
      } finally {
        this.isLoading = false;
      }
    },
    handleRemoveService(service) {
      this.updateInput(this.value.filter(({ id }) => service.id !== id));
    },
    handleRemoveAllService() {
      this.confirmDialog.isShow = true;
    },
    handleConfirmRemoveAll(confirmed) {
      this.confirmDialog.isShow = false;
      if (confirmed) {
        this.updateInput([]);
      }
    },
    updateInput(value) {
      this.$emit("input", value);
    },
    compareService(serviceA, serviceB) {
      return serviceA.id === serviceB.id;
    },
    handleQueryChange(value) {
      this.query = value;
    },
    watchQuery: debounce(function watchQuery() {
      this.resetData();
      this.setupServices();
      this.$refs.searchableServiceList.resetLoading();
    }, 1.5),
    resetData() {
      this.allServices = [];
      this.totalServices = 1;
      this.page = 1;
      this.hasMoreServices = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.service-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;

  > .search-list {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  > .selected-list {
    border-left-width: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.service-selector.disabled {
  display: block;

  > .selected-list {
    max-width: 600px;
    border-left-width: 1px;
    border-radius: $border-radius;
  }
}
</style>
